import React, { useState } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faMoneyBillWave,
    faReceipt,
    faChartLine,
    faBalanceScale,
    faGavel,
    faFileContract,
    faShieldAlt,
    faUsers,
    faLightbulb,
    faAward,
    faCheckCircle,
    faChevronRight,
    faIndustry,
    faStar,
    faUserTie,
    faPhone,
    faEnvelope,
    faCalendarAlt,
    faCertificate,
    faBuilding,
    faGlobe,
    faCalculator,
    faFileSignature,
    faPercent,
    faLandmark,
    faClipboardList,
    faUniversity,
    faHandshake,
    faSearch,
    faFileAlt,
    faScaleBalanced,
    faArrowTrendUp,
    faMoneyCheckDollar
} from "@fortawesome/free-solid-svg-icons";
import { Cta } from "../components/Cta";
import { useNavigate } from "react-router-dom";

// Animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.1 }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.8,
            ease: [0.22, 1, 0.36, 1]
        }
    }
};

const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.8, ease: "easeOut" }
    }
};

const directTaxServices = [
    {
        icon: faCalculator,
        title: "Income Tax Advisory",
        description: "Comprehensive advisory on income tax planning, compliance, and optimization strategies.",
        color: "gold",
        subservices: [
            "Tax Planning & Strategy",
            "Return Filing & Compliance",
            "Tax Audit Representation",
            "Advance Tax Planning"
        ],
        stat: "1000+"
    },
    {
        icon: faFileAlt,
        title: "Transfer Pricing",
        description: "Expert guidance on transfer pricing documentation, compliance, and dispute resolution.",
        color: "primary-blue",
        subservices: [
            "Transfer Pricing Documentation",
            "APA Applications",
            "International Transactions",
            "TP Audits & Assessments"
        ],
        stat: "200+"
    },
    {
        icon: faSearch,
        title: "Tax Assessments & Appeals",
        description: "Representation in income tax assessments, appeals, and litigation before tribunals.",
        color: "gold",
        subservices: [
            "Assessment Proceedings",
            "CIT(A) Appeals",
            "ITAT Appeals",
            "High Court & Supreme Court"
        ],
        stat: "500+"
    },
    {
        icon: faMoneyCheckDollar,
        title: "Corporate Taxation",
        description: "Specialized tax advisory for corporations, companies, and business entities.",
        color: "primary-blue",
        subservices: [
            "Corporate Tax Planning",
            "MAT & AMT Advisory",
            "Dividend Distribution Tax",
            "Corporate Restructuring"
        ],
        stat: "300+"
    }
];

const indirectTaxServices = [
    {
        icon: faReceipt,
        title: "GST Advisory & Compliance",
        description: "Comprehensive GST advisory, registration, returns, and compliance management.",
        color: "gold",
        subservices: [
            "GST Registration & Migration",
            "Monthly/Quarterly Returns",
            "GST Audits",
            "E-way Bill Compliance"
        ],
        stat: "800+"
    },
    {
        icon: faBalanceScale,
        title: "GST Litigation",
        description: "Representation in GST disputes, appeals, and litigation before authorities.",
        color: "primary-blue",
        subservices: [
            "Assessment Proceedings",
            "Appeals to Appellate Authority",
            "GST Tribunal Matters",
            "Writ Petitions"
        ],
        stat: "400+"
    },
    // {
    //     icon: faPercent,
    //     title: "Customs & Excise",
    //     description: "Expert advice on customs duty, excise, and international trade taxation.",
    //     color: "gold",
    //     subservices: [
    //         "Customs Valuation",
    //         "Export-Import Tax Matters",
    //         "Excise Duty Compliance",
    //         "Customs Litigation"
    //     ],
    //     stat: "250+"
    // },
    {
        icon: faFileContract,
        title: "Indirect Tax Planning",
        description: "Strategic planning for GST, customs, and other indirect tax optimization.",
        color: "primary-blue",
        subservices: [
            "GST Rate Optimization",
            "Input Tax Credit Planning",
            "Export Benefits",
            "Supply Chain Optimization"
        ],
        stat: "150+"
    }
];

const taxDisputes = [
    {
        type: "Income Tax Disputes",
        icon: faGavel,
        description: "Resolution of income tax disputes through appeals and litigation.",
        forums: ["CIT(A)", "ITAT", "High Court", "Supreme Court"],
        successRate: "85%",
        color: "gold"
    },
    {
        type: "GST Litigation",
        icon: faScaleBalanced,
        description: "Representation in GST disputes and appellate proceedings.",
        forums: ["GST Authority", "Appellate Authority", "GST Tribunal", "High Court"],
        successRate: "80%",
        color: "primary-blue"
    },
    {
        type: "Transfer Pricing",
        icon: faGlobe,
        description: "Resolution of transfer pricing disputes and APA applications.",
        forums: ["TP Officer", "DRP", "ITAT", "Court Appeals"],
        successRate: "75%",
        color: "gold"
    },
    // {
    //     type: "Customs Disputes",
    //     icon: faLandmark,
    //     description: "Customs duty disputes and international trade tax matters.",
    //     forums: ["Customs Authority", "CESTAT", "High Court", "Supreme Court"],
    //     successRate: "70%",
    //     color: "primary-blue"
    // }
];

const clientSectors = [
    {
        sector: "Corporate & Business",
        icon: faBuilding,
        description: "Tax advisory for corporations, partnerships, and business entities.",
        services: ["Corporate Tax", "GST Compliance", "Transfer Pricing", "Tax Audits"],
        color: "gold"
    },
    {
        sector: "Manufacturing",
        icon: faIndustry,
        description: "Tax solutions for manufacturing units and industrial enterprises.",
        services: ["Excise Duty", "GST on Manufacture", "Export Benefits", "Tax Incentives"],
        color: "primary-blue"
    },
    {
        sector: "International Trade",
        icon: faGlobe,
        description: "Cross-border taxation and international trade tax matters.",
        services: ["Customs Duty", "Import-Export Taxes", "FTA Benefits", "Transfer Pricing"],
        color: "gold"
    },
    {
        sector: "Startups & MSMEs",
        icon: faChartLine,
        description: "Tax planning and compliance for startups and small businesses.",
        services: ["Startup Tax Benefits", "GST Compliance", "Tax Registration", "Compliance Advisory"],
        color: "primary-blue"
    }
];

const taxLaws = [
    {
        law: "Income Tax Act, 1961",
        icon: faCalculator,
        description: "Comprehensive legislation governing direct taxation in India.",
        keyAreas: ["Income Computation", "Deductions", "Assessment", "Appeals"],
        color: "gold"
    },
    {
        law: "GST Acts, 2017",
        icon: faReceipt,
        description: "Goods and Services Tax legislation for indirect taxation.",
        keyAreas: ["CGST", "SGST", "IGST", "GST Compliance"],
        color: "primary-blue"
    },
    {
        law: "Customs Act, 1962",
        icon: faLandmark,
        description: "Legislation governing customs duties and international trade taxes.",
        keyAreas: ["Customs Valuation", "Import Duty", "Export Benefits", "Customs Procedures"],
        color: "gold"
    },
    {
        law: "Transfer Pricing Regulations",
        icon: faGlobe,
        description: "Rules governing cross-border transactions between associated enterprises.",
        keyAreas: ["ALP Determination", "Documentation", "APA", "Penalties"],
        color: "primary-blue"
    }
];

const caseStudies = [
    {
        title: "₹50 Crore Transfer Pricing Dispute",
        client: "Multinational Corporation",
        issue: "Transfer pricing adjustment on international transactions",
        solution: "APA negotiation and strategic litigation",
        result: "Settlement with minimal adjustment",
        savings: "₹15 Crore",
        icon: faGlobe
    },
    {
        title: "Complex GST Refund Matter",
        client: "Export-Oriented Unit",
        issue: "Blocked GST refund of ₹25 Crore",
        solution: "Comprehensive representation before authorities",
        result: "Full refund sanctioned",
        duration: "6 months",
        icon: faMoneyBillWave
    },
    {
        title: "Income Tax Assessment Appeal",
        client: "High Net Worth Individual",
        issue: "₹10 Crore addition in assessment",
        solution: "Strategic appeal before CIT(A) and ITAT",
        result: "Addition deleted",
        savings: "₹3 Crore",
        icon: faGavel
    },
    {
        title: "Customs Duty Litigation",
        client: "Importer of Machinery",
        issue: "Customs valuation dispute of ₹5 Crore",
        solution: "Expert representation before CESTAT",
        result: "Favorable ruling obtained",
        duration: "8 months",
        icon: faLandmark
    }
];

const whyChooseUs = [
   
    {
        reason: "Dual Expertise",
        description: "Specialization in both direct and indirect taxation",
        icon: faBalanceScale,
        color: "gold"
    },
    {
        reason: "Strategic Tax Planning",
        description: "Proactive tax planning and optimization strategies",
        icon: faLightbulb,
        color: "gold"
    },
    {
        reason: "Litigation Excellence",
        description: "Strong track record in tax dispute resolution",
        icon: faGavel,
        color: "gold"
    }
];

export const TaxationLaw = () => {
    const [activeTaxType, setActiveTaxType] = useState("direct");
    const [activeService, setActiveService] = useState(0);
   const navigate=useNavigate();
    return (
        <div style={{
            backgroundColor: "var(--cream)",
            minHeight: "100vh",
            padding: "130px 0 60px",
            position: "relative"
        }}>
            {/* Background decorative elements */}
            <div className="position-fixed top-0 start-0 w-100 h-100" style={{ zIndex: 0, pointerEvents: "none", overflow: "hidden" }}>
                <div className="position-absolute top-0 end-0 w-25 h-25"
                    style={{
                        background: "radial-gradient(circle, var(--gold) 0%, transparent 70%)",
                        opacity: 0.03,
                        transform: "translate(30%, -30%)"
                    }}
                ></div>
                <div className="position-absolute bottom-0 start-0 w-30 h-30"
                    style={{
                        background: "radial-gradient(circle, var(--primary-blue) 0%, transparent 70%)",
                        opacity: 0.03,
                        transform: "translate(-30%, 30%)"
                    }}
                ></div>
            </div>

            <div className="container position-relative" style={{ zIndex: 1 }}>
                {/* Hero Section */}
                <motion.div
                    className="text-center mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <motion.div variants={fadeInUp}>
                        <span className="d-inline-block px-4 py-2 rounded-pill mb-3"
                            style={{
                                color: "var(--gold)",
                                fontWeight: "600",
                                letterSpacing: "2px",
                                fontSize: "0.9rem",
                                textTransform: "uppercase",
                                border: "1px solid var(--gold-light)",
                                background: "rgba(184, 134, 11, 0.05)",
                                backdropFilter: "blur(4px)"
                            }}
                        >
                            Tax Law Specialists
                        </span>


                        <motion.h4
                            className="fw-bold display-6 display-md-5 mt-2"
                            variants={fadeInUp}
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                fontWeight: 600,
                                color: 'var(--primary-dark)',
                                background: "linear-gradient(135deg, var(--primary-dark) 0%, var(--gold) 100%)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                backgroundClip: "text"
                            }}
                        >
                            Taxation Law (Direct & Indirect)
                        </motion.h4>
                    </motion.div>

                    <motion.p
                        className="fs-6 fs-md-5 mx-auto mb-3 mb-md-4"
                        variants={fadeInUp}
                        style={{
                            maxWidth: "750px",
                            color: "var(--text-primary)",
                            lineHeight: 1.7
                        }}
                    >
                        Comprehensive tax advisory and litigation services for both direct and indirect taxation.
                        Our Team provide strategic solutions for tax planning,
                        compliance, and dispute resolution.
                    </motion.p>
                </motion.div>

                {/* Tax Type Toggle */}
                <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <div className="d-flex justify-content-center mb-4">
                            <div className="btn-group" role="group">
                                <motion.button
                                    type="button"
                                    className={`btn btn-lg px-5 py-3 ${activeTaxType === 'direct' ? 'active' : ''}`}
                                    onClick={() => setActiveTaxType('direct')}
                                    style={{
                                        backgroundColor: activeTaxType === 'direct' ? 'var(--gold)' : 'transparent',
                                        color: activeTaxType === 'direct' ? 'var(--white)' : 'var(--gold)',
                                        border: `2px solid var(--gold)`,
                                        borderRight: activeTaxType === 'direct' ? '2px solid var(--gold)' : '1px solid var(--gold-light)',
                                        fontWeight: "600",
                                        transition: "var(--transition)"
                                    }}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <FontAwesomeIcon icon={faCalculator} className="me-2" />
                                    Direct Taxation
                                </motion.button>

                                <motion.button
                                    type="button"
                                    className={`btn btn-lg px-5 py-3 ${activeTaxType === 'indirect' ? 'active' : ''}`}
                                    onClick={() => setActiveTaxType('indirect')}
                                    style={{
                                        backgroundColor: activeTaxType === 'indirect' ? 'var(--primary-blue)' : 'transparent',
                                        color: activeTaxType === 'indirect' ? 'var(--white)' : 'var(--primary-blue)',
                                        border: `2px solid var(--primary-blue)`,
                                        borderLeft: activeTaxType === 'indirect' ? '2px solid var(--primary-blue)' : '1px solid var(--primary-blue-light)',
                                        fontWeight: "600",
                                        transition: "var(--transition)"
                                    }}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <FontAwesomeIcon icon={faReceipt} className="me-2" />
                                    Indirect Taxation
                                </motion.button>
                            </div>
                        </div>

                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            {activeTaxType === 'direct' ? (
                                <>
                                    <FontAwesomeIcon icon={faCalculator} className="me-3" style={{ color: "var(--gold)" }} />
                                    Direct Tax Services
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faReceipt} className="me-3" style={{ color: "var(--primary-blue)" }} />
                                    Indirect Tax Services
                                </>
                            )}
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            {activeTaxType === 'direct'
                                ? "Expert legal services for income tax, corporate tax, and transfer pricing"
                                : "Comprehensive solutions for GST, customs, and other indirect taxes"}
                        </p>
                    </div>

                    {/* Services Grid */}
                    <div className="row g-4">
                        {(activeTaxType === 'direct' ? directTaxServices : indirectTaxServices).map((service, index) => (
                            <motion.div
                                key={index}
                                className="col-md-6 col-lg-3"
                                variants={itemVariants}
                                onMouseEnter={() => setActiveService(index)}
                            >
                                <motion.div
                                    className="h-100 p-4 rounded-4 position-relative overflow-hidden"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: `1px solid var(--${service.color}-light)`,
                                        boxShadow: "var(--shadow-soft)",
                                        transition: "var(--transition)"
                                    }}
                                    whileHover={{
                                        y: -8,
                                        boxShadow: "var(--shadow-medium)",
                                        borderColor: `var(--${service.color})`
                                    }}
                                >
                                    <div className="position-relative z-1 h-100 d-flex flex-column">
                                        {/* Icon & Stat */}
                                        <div className="d-flex align-items-center justify-content-between mb-4">
                                            <motion.div
                                                className="p-3 rounded-3"
                                                style={{
                                                    backgroundColor: `rgba(var(--${service.color}-rgb), 0.1)`,
                                                    color: `var(--${service.color})`
                                                }}
                                                whileHover={{ rotate: 360 }}
                                                transition={{ duration: 0.6, ease: "easeInOut" }}
                                            >
                                                <FontAwesomeIcon icon={service.icon} style={{ fontSize: "1.5rem" }} />
                                            </motion.div>

                                            {/* <motion.div
                                                className="display-6 fw-bold"
                                                style={{
                                                    color: `var(--${service.color})`,
                                                    fontFamily: "'Playfair Display', serif"
                                                }}
                                                animate={{
                                                    scale: activeService === index ? 1.1 : 1
                                                }}
                                                transition={{ type: "spring", stiffness: 300 }}
                                            >
                                                {service.stat}
                                            </motion.div> */}
                                        </div>

                                        {/* Title & Description */}
                                        <h5 className="fw-bold mb-3" style={{ color: "var(--primary-dark)" }}>
                                            {service.title}
                                        </h5>

                                        <p className="mb-4 flex-grow-1 small"
                                            style={{
                                                color: "var(--text-primary)",
                                                lineHeight: 1.6
                                            }}
                                        >
                                            {service.description}
                                        </p>

                                        {/* Subservices */}
                                        <div className="mt-auto pt-3 border-top"
                                            style={{ borderColor: `var(--${service.color}-light)` }}
                                        >
                                            <div className="vstack gap-2">
                                                {service.subservices.map((subservice, idx) => (
                                                    <motion.div
                                                        key={idx}
                                                        className="d-flex align-items-center gap-2"
                                                        initial={{ opacity: 0, x: -10 }}
                                                        whileInView={{ opacity: 1, x: 0 }}
                                                        viewport={{ once: true }}
                                                        transition={{ delay: idx * 0.1 }}
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={faChevronRight}
                                                            style={{
                                                                color: `var(--${service.color})`,
                                                                fontSize: "0.7rem"
                                                            }}
                                                        />
                                                        <span className="small" style={{ color: "var(--text-primary)" }}>
                                                            {subservice}
                                                        </span>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Tax Disputes */}
                <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faGavel} className="me-3" style={{ color: "var(--gold)" }} />
                            Tax Dispute Resolution
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            Expert representation in tax disputes and litigation across all forums
                        </p>
                    </div>

                    <div className="row g-4 d-flex justify-content-center">
                        {taxDisputes.map((dispute, index) => (
                            <motion.div
                                key={index}
                                className="col-md-6 col-lg-3"
                                variants={itemVariants}
                            >
                                <motion.div
                                    className="p-4 rounded-4 h-100"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: `2px solid var(--${dispute.color}-light)`,
                                        boxShadow: "var(--shadow-soft)",
                                        transition: "var(--transition)"
                                    }}
                                    whileHover={{
                                        y: -5,
                                        boxShadow: "var(--shadow-medium)",
                                        borderColor: `var(--${dispute.color})`
                                    }}
                                >
                                    <div className="text-center mb-3">
                                        <div className="p-3 rounded-circle d-inline-block"
                                            style={{
                                                backgroundColor: `rgba(var(--${dispute.color}-rgb), 0.1)`,
                                                color: `var(--${dispute.color})`
                                            }}
                                        >
                                            <FontAwesomeIcon icon={dispute.icon} style={{ fontSize: "1.5rem" }} />
                                        </div>
                                    </div>

                                    <h5 className="fw-bold text-center mb-3" style={{ color: "var(--primary-dark)" }}>
                                        {dispute.type}
                                    </h5>

                                    <p className="small text-center mb-4" style={{
                                        color: "var(--text-primary)",
                                        lineHeight: 1.6
                                    }}>
                                        {dispute.description}
                                    </p>

                                    <div className="mb-4">
                                        <div className="small fw-bold mb-2" style={{ color: "var(--primary-dark)" }}>
                                            Appellate Forums:
                                        </div>
                                        <div className="vstack gap-1">
                                            {dispute.forums.map((forum, idx) => (
                                                <div key={idx} className="d-flex align-items-center gap-2">
                                                    <FontAwesomeIcon
                                                        icon={faChevronRight}
                                                        style={{
                                                            color: `var(--${dispute.color})`,
                                                            fontSize: "0.7rem"
                                                        }}
                                                    />
                                                    <span className="small" style={{ color: "var(--text-primary)" }}>
                                                        {forum}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* <div className="pt-3 border-top text-center"
                                        style={{ borderColor: `var(--${dispute.color}-light)` }}
                                    >
                                        <div className="display-6 fw-bold mb-1" style={{ color: `var(--${dispute.color})` }}>
                                            {dispute.successRate}
                                        </div>
                                        <div className="small" style={{ color: "var(--text-primary)" }}>
                                            Success Rate
                                        </div>
                                    </div> */}
                                </motion.div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Client Sectors */}
                {/* <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faUsers} className="me-3" style={{ color: "var(--gold)" }} />
                            Sector-Specific Tax Expertise
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            Tailored tax solutions for different industries and business sectors
                        </p>
                    </div>

                    <div className="row g-4">
                        {clientSectors.map((sector, index) => (
                            <motion.div
                                key={index}
                                className="col-md-6 col-lg-3"
                                variants={itemVariants}
                            >
                                <motion.div
                                    className="p-4 rounded-4 h-100"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: `2px solid var(--${sector.color}-light)`,
                                        boxShadow: "var(--shadow-soft)",
                                        transition: "var(--transition)"
                                    }}
                                    whileHover={{
                                        y: -5,
                                        boxShadow: "var(--shadow-medium)",
                                        borderColor: `var(--${sector.color})`
                                    }}
                                >
                                    <div className="text-center mb-3">
                                        <div className="p-3 rounded-circle d-inline-block"
                                            style={{
                                                backgroundColor: `rgba(var(--${sector.color}-rgb), 0.1)`,
                                                color: `var(--${sector.color})`
                                            }}
                                        >
                                            <FontAwesomeIcon icon={sector.icon} style={{ fontSize: "1.5rem" }} />
                                        </div>
                                    </div>

                                    <h5 className="fw-bold text-center mb-3" style={{ color: "var(--primary-dark)" }}>
                                        {sector.sector}
                                    </h5>

                                    <p className="small text-center mb-4" style={{
                                        color: "var(--text-primary)",
                                        lineHeight: 1.6
                                    }}>
                                        {sector.description}
                                    </p>

                                    <div className="vstack gap-2">
                                        {sector.services.map((service, idx) => (
                                            <div key={idx} className="d-flex align-items-center gap-2">
                                                <FontAwesomeIcon
                                                    icon={faCheckCircle}
                                                    style={{
                                                        color: `var(--${sector.color})`,
                                                        fontSize: "0.7rem"
                                                    }}
                                                />
                                                <span className="small" style={{ color: "var(--text-primary)" }}>
                                                    {service}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </motion.div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div> */}

                {/* Key Tax Laws */}
                <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faScaleBalanced} className="me-3" style={{ color: "var(--gold)" }} />
                            Key Tax Legislations
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            Expertise in major taxation laws and regulations
                        </p>
                    </div>

                    <div className="row g-4">
                        {taxLaws.map((law, index) => (
                            <motion.div
                                key={index}
                                className="col-md-6 col-lg-3"
                                variants={itemVariants}
                            >
                                <motion.div
                                    className="p-4 rounded-4 h-100"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: `1px solid var(--${law.color}-light)`,
                                        boxShadow: "var(--shadow-soft)",
                                        transition: "var(--transition)"
                                    }}
                                    whileHover={{
                                        y: -5,
                                        boxShadow: "var(--shadow-medium)"
                                    }}
                                >
                                    <div className="d-flex align-items-center gap-3 mb-3">
                                        <div className="p-2 rounded-3 flex-shrink-0"
                                            style={{
                                                backgroundColor: `rgba(var(--${law.color}-rgb), 0.1)`,
                                                color: `var(--${law.color})`
                                            }}
                                        >
                                            <FontAwesomeIcon icon={law.icon} />
                                        </div>
                                        <h6 className="fw-bold mb-0" style={{ color: "var(--primary-dark)" }}>
                                            {law.law}
                                        </h6>
                                    </div>

                                    <p className="small mb-4" style={{
                                        color: "var(--text-primary)",
                                        lineHeight: 1.5
                                    }}>
                                        {law.description}
                                    </p>

                                    <div className="mt-3 pt-3 border-top"
                                        style={{ borderColor: `var(--${law.color}-light)` }}
                                    >
                                        <div className="small fw-bold mb-2" style={{ color: "var(--primary-dark)" }}>
                                            Key Areas:
                                        </div>
                                        <div className="d-flex flex-wrap gap-2">
                                            {law.keyAreas.map((area, idx) => (
                                                <span key={idx} className="badge px-2 py-1"
                                                    style={{
                                                        backgroundColor: `rgba(var(--${law.color}-rgb), 0.1)`,
                                                        color: `var(--${law.color})`,
                                                        fontSize: "0.75rem"
                                                    }}
                                                >
                                                    {area}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </motion.div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Case Studies */}
                {/* <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faAward} className="me-3" style={{ color: "var(--gold)" }} />
                            Tax Case Studies
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            Successful tax dispute resolutions and significant savings achieved
                        </p>
                    </div>

                    <div className="row g-4">
                        {caseStudies.map((caseStudy, index) => (
                            <motion.div
                                key={index}
                                className="col-md-6 col-lg-3"
                                variants={itemVariants}
                            >
                                <motion.div
                                    className="p-4 rounded-4 h-100"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: "1px solid var(--gold-light)",
                                        boxShadow: "var(--shadow-soft)",
                                        transition: "var(--transition)"
                                    }}
                                    whileHover={{
                                        y: -5,
                                        boxShadow: "var(--shadow-medium)",
                                        borderColor: "var(--gold)"
                                    }}
                                >
                                    <div className="mb-3">
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                            <div className="p-2 rounded-3 flex-shrink-0"
                                                style={{
                                                    backgroundColor: "rgba(var(--gold-rgb), 0.1)",
                                                    color: "var(--gold)"
                                                }}
                                            >
                                                <FontAwesomeIcon icon={caseStudy.icon} />
                                            </div>
                                            <h6 className="fw-bold mb-0" style={{ color: "var(--primary-dark)" }}>
                                                {caseStudy.title}
                                            </h6>
                                        </div>
                                        <div className="small mb-3" style={{ color: "var(--gold)" }}>
                                            Client: {caseStudy.client}
                                        </div>
                                    </div>

                                    <div className="vstack gap-3 mb-4">
                                        <div>
                                            <div className="small fw-bold mb-1" style={{ color: "var(--primary-dark)" }}>
                                                Issue
                                            </div>
                                            <div className="small" style={{ color: "var(--text-primary)" }}>
                                                {caseStudy.issue}
                                            </div>
                                        </div>

                                        <div>
                                            <div className="small fw-bold mb-1" style={{ color: "var(--primary-dark)" }}>
                                                Solution
                                            </div>
                                            <div className="small" style={{ color: "var(--text-primary)" }}>
                                                {caseStudy.solution}
                                            </div>
                                        </div>

                                        <div>
                                            <div className="small fw-bold mb-1" style={{ color: "var(--primary-dark)" }}>
                                                Result
                                            </div>
                                            <div className="small fw-bold" style={{ color: "var(--green-accent)" }}>
                                                {caseStudy.result}
                                            </div>
                                        </div>

                                        {caseStudy.savings && (
                                            <div>
                                                <div className="small fw-bold mb-1" style={{ color: "var(--primary-dark)" }}>
                                                    Tax Savings
                                                </div>
                                                <div className="small fw-bold" style={{ color: "var(--gold)" }}>
                                                    {caseStudy.savings}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div> */}

                {/* Why Choose RNA */}
                <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="p-4 p-md-5 rounded-4"
                        style={{
                            backgroundColor: "var(--primary-dark)",
                            backgroundImage: "linear-gradient(135deg, var(--primary-dark) 0%, var(--primary-blue) 100%)",
                            border: "1px solid var(--gold-light)"
                        }}
                    >
                        <div className="row align-items-center">
                            <div className="col-lg-8">
                                <motion.div variants={fadeInUp}>
                                    <h3 className="fw-bold mb-3" style={{ color: "var(--white)" }}>
                                        <FontAwesomeIcon icon={faUserTie} className="me-3" style={{ color: "var(--gold)" }} />
                                        Why Choose RNA for Taxation?
                                    </h3>

                                    <div className="row g-4 mb-4">
                                        {whyChooseUs.map((reason, index) => (
                                            <div key={index} className="col-md-6">
                                                <div className="d-flex align-items-start gap-3 mb-3">
                                                    <div className="p-2 rounded-3 flex-shrink-0"
                                                        style={{
                                                            backgroundColor: `rgba(var(--${reason.color}-rgb), 0.2)`,
                                                            color: `var(--${reason.color})`
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={reason.icon} />
                                                    </div>
                                                    <div>
                                                        <div className="fw-bold mb-1" style={{ color: "var(--white)" }}>
                                                            {reason.reason}
                                                        </div>
                                                        <div className="small" style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                                                            {reason.description}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* <div className="row g-4">
                                        <div className="col-md-4">
                                            <div className="text-center">
                                                <div className="display-4 fw-bold mb-1" style={{ color: "var(--gold)" }}>
                                                    1000+
                                                </div>
                                                <div className="small" style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                                                    Tax Matters Handled
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="text-center">
                                                <div className="display-4 fw-bold mb-1" style={{ color: "var(--gold)" }}>
                                                    85%
                                                </div>
                                                <div className="small" style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                                                    Success Rate
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="text-center">
                                                <div className="display-4 fw-bold mb-1" style={{ color: "var(--gold)" }}>
                                                    15+
                                                </div>
                                                <div className="small" style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                                                    Years Experience
                                                </div>
                                            </div>
                                        </div>
                                    </div> */}
                                </motion.div>
                            </div>

                            <div className="col-lg-4 text-center mt-4 mt-lg-0">
                                <motion.div
                                    className="p-4 rounded-4"
                                    style={{
                                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                                        backdropFilter: "blur(10px)",
                                        border: "1px solid rgba(255, 255, 255, 0.2)"
                                    }}
                                    variants={itemVariants}
                                >
                                    <FontAwesomeIcon
                                        icon={faUserTie}
                                        className="mb-3"
                                        style={{
                                            fontSize: "3rem",
                                            color: "var(--gold)"
                                        }}
                                    />
                                    <h5 className="fw-bold mb-3" style={{ color: "var(--white)" }}>
                                        Expert Tax Consultation
                                    </h5>
                                    <p className="small mb-4" style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                                        Schedule a consultation with our Team.
                                    </p>
                                    <motion.button
                                      onClick={() => navigate("/contact")}
                                        className="btn w-100 py-3"
                                        style={{
                                            backgroundColor: "var(--gold)",
                                            color: "var(--primary-dark)",
                                            fontWeight: "600",
                                            border: "none"
                                        }}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.98 }}
                                    >
                                        <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                                        Book Consultation
                                    </motion.button>
                                </motion.div>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* CTA Section */}
             
                <Cta Title="Need Expert Tax Advice?" description=" Contact our taxation law specialists for personalized consultation"/>
            </div>
        </div>
    );
};

